import { useState, useEffect, useRef, useMemo } from 'react';
import { useAddressAutocomplete as useAutocompleteQuery } from '@cfleet/api-client';
import { useAuthConfig } from './useAuthConfig';

interface AddressSuggestion {
  formatted_address: string;
  address: {
    street: string;
    unit?: string;
    city: string;
    province?: string;
    zip: string;
  };
  latitude?: number;
  longitude?: number;
}

interface UseAddressSearchResult {
  query: string;
  setQuery: (q: string) => void;
  suggestions: AddressSuggestion[];
  isLoading: boolean;
  showDropdown: boolean;
  setShowDropdown: (show: boolean) => void;
}

const DEBOUNCE_MS = 600;
const MIN_CHARS = 10;

const EMPTY_SUGGESTIONS: AddressSuggestion[] = [];

/**
 * Hook for address autocomplete with debounce.
 *
 * Wraps the api-client useAddressAutocomplete with:
 * - 600ms debounce to avoid excessive API calls
 * - Minimum 10 character threshold
 * - Dropdown visibility management
 */
export function useAddressSearch(): UseAddressSearchResult {
  const authConfig = useAuthConfig();
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState<string | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Debounce the query
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);

    if (query.length >= MIN_CHARS) {
      timerRef.current = setTimeout(() => {
        setDebouncedQuery(query);
      }, DEBOUNCE_MS);
    } else {
      setDebouncedQuery(null);
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [query]);

  const { data, isPending } = useAutocompleteQuery(debouncedQuery, { authConfig });

  // Stabilize suggestions reference to prevent unnecessary re-renders
  const suggestions = useMemo(
    () => data?.suggestions ?? EMPTY_SUGGESTIONS,
    [data]
  );

  // Show dropdown when we have suggestions
  const suggestionsCount = suggestions.length;
  useEffect(() => {
    if (suggestionsCount > 0 && query.length >= MIN_CHARS) {
      setShowDropdown(true);
    }
  }, [suggestionsCount, query]);

  return {
    query,
    setQuery,
    suggestions,
    isLoading: isPending && !!debouncedQuery,
    showDropdown,
    setShowDropdown,
  };
}
